'use client';
import {
  mayChooseUserType,
  SELF_SERVICE_USER_TYPES,
  SelfServiceUserType,
  userTypeOptionsFor,
} from '@/layers/support/authz/userType';
import logger from '@/layers/support/monitoring/logger';
import { UserGender, UserType } from '@/types/modelEnums';
import { ProviderProfile, User } from '@/types/models';
import type { FetchUserAttributesOutput } from 'aws-amplify/auth';
import { usePathname } from 'next/navigation';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { getProfile, setUserType } from '../services/users';
import { ensureAmplifyConfigured } from '../utils/ensureAmplifyConfigured';
import {
  forgetAllPendingUserTypes,
  forgetPendingUserType,
  readPendingUserType,
} from '../utils/pendingUserType';
import { providerProfileNeedsCompletion } from '../utils/profileUtils';
import { useGlobalState } from './GlobalStateContext';

const LOG_MODULE = 'FE [Presentation / Context / AuthContext]';

// Storage keys
const AUTH_CACHE_KEY = 'r2h_auth_cache';

interface AuthCache {
  user: User | null;
  timestamp: number;
  // We don't cache full profile for security reasons, just user basic info
}

interface AuthContextType {
  isProfileLoading: boolean;
  refreshProfile: () => Promise<void>;
  refreshUserAttributes: () => Promise<void>;
  userRole: string | null;
  needsProviderProfileCompletion: boolean;
  /** Signed in with no account type and none stored from sign-up, so the user must pick one. */
  needsUserType: boolean;
  /** The types that user may pick; Provider needs an institutional email. */
  userTypeOptions: SelfServiceUserType[];
  chooseUserType: (type: SelfServiceUserType) => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper to load cached user data with type safety
const loadCachedAuthState = (): AuthCache | null => {
  if (typeof window === 'undefined') return null;
  try {
    const cachedData = localStorage.getItem(AUTH_CACHE_KEY);
    if (!cachedData) return null;

    const parsed = JSON.parse(cachedData) as AuthCache;
    // Check if cache is too old (24 hours)
    const cacheAge = Date.now() - parsed.timestamp;
    if (cacheAge > 24 * 60 * 60 * 1000) {
      localStorage.removeItem(AUTH_CACHE_KEY);
      return null;
    }
    return parsed;
  } catch (e) {
    console.error('Error loading cached auth state', e);
    return null;
  }
};

// Helper to save cache
const saveAuthCache = (user: User | null) => {
  if (typeof window === 'undefined') return;
  try {
    if (!user) {
      localStorage.removeItem(AUTH_CACHE_KEY);
      return;
    }

    const cache: AuthCache = {
      user,
      timestamp: Date.now(),
    };
    localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    console.error('Error saving auth cache', e);
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const pathname = usePathname();
  const {
    user: globalUser,
    profile: globalProfile,
    setUser,
    setProfile,
    setIsProfileLoaded,
  } = useGlobalState();
  const [isProfileLoading, setIsProfileLoading] = useState<boolean>(false);
  const [userAttributes, setUserAttributes] = useState<any>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [needsProviderProfileCompletion, setNeedsProviderProfileCompletion] =
    useState<boolean>(false);
  const [needsUserType, setNeedsUserType] = useState<boolean>(false);
  const [userTypeOptions, setUserTypeOptions] = useState<SelfServiceUserType[]>([
    ...SELF_SERVICE_USER_TYPES,
  ]);
  const settlingRef = useRef<Promise<FetchUserAttributesOutput | null> | null>(null);
  const profileFetchedRef = useRef(false);
  const cacheInitializedRef = useRef(false);
  const cognitoUserIdRef = useRef<string | null>(null);

  const isAppRoute =
    pathname.startsWith('/patient') ||
    pathname.startsWith('/provider') ||
    pathname.startsWith('/sponsor') ||
    pathname.startsWith('/users') ||
    pathname.startsWith('/admin');

  // Initialize from cache on mount - exactly once
  useEffect(() => {
    if (cacheInitializedRef.current || globalUser) return;

    const cachedState = loadCachedAuthState();
    if (cachedState?.user && !globalUser) {
      logger.debug(`${LOG_MODULE} Initializing from cache`, cachedState);
      setUser(cachedState.user);
      // We'll set userRole from the cached user
      if (cachedState.user.role) {
        setUserRole(cachedState.user.role);
      }
    }

    cacheInitializedRef.current = true;
  }, [globalUser, setUser]);

  // Logged from an effect, not during render. This provider is server-rendered, and a
  // render-phase call would serialise the user and profile into the server log on every
  // request — the values are null server-side today, but this keeps it that way.
  useEffect(() => {
    logger.debug(`${LOG_MODULE}`, {
      isProfileLoading,
      globalUser,
      globalProfile,
      userAttributes,
      userRole,
      fromCache: cacheInitializedRef.current,
    });
  }, [isProfileLoading, globalUser, globalProfile, userAttributes, userRole]);

  const fetchProfile = useCallback(
    async (userId: string, userType: UserType) => {
      if (profileFetchedRef.current) return;
      // Prevent re-entry from the useEffect hook.
      profileFetchedRef.current = true;
      setIsProfileLoading(true);

      try {
        let fetchedProfile = await getProfile(userId, userType);

        // If the profile is not found or is incomplete (e.g., no ID), retry a few times.
        // This is to handle the delay in profile creation after signup.
        if (!fetchedProfile?.id) {
          logger.debug(
            `${LOG_MODULE} Profile not found or incomplete for user ${userId}. Retrying...`,
          );
          for (let i = 0; i < 3; i++) {
            await new Promise(res => setTimeout(res, 1000 * (i + 1))); // 1s, 2s, 3s delay
            fetchedProfile = await getProfile(userId, userType);
            if (fetchedProfile?.id) {
              logger.debug(`${LOG_MODULE} Profile found for user ${userId} after retry.`);
              break;
            }
          }
        }

        setProfile(fetchedProfile);
      } catch (error) {
        console.error('Error fetching profile:', error);
        logger.error(`${LOG_MODULE} Error fetching profile`, {
          error,
          userId,
          userType,
        });
      } finally {
        setIsProfileLoading(false);
        setIsProfileLoaded(true);
      }
    },
    [setProfile, setIsProfileLoaded],
  );

  const refreshProfile = useCallback(async () => {
    if (globalUser) {
      profileFetchedRef.current = false;
      await fetchProfile(globalUser.id, globalUser.type);
    }
  }, [globalUser, fetchProfile]);

  const clearAuthState = useCallback(() => {
    setNeedsUserType(false);
    if (!(globalUser || globalProfile)) return;
    logger.debug(`${LOG_MODULE} Clearing auth state`);
    setUser(null);
    setProfile(null);
    setIsProfileLoaded(false);
    setUserAttributes(null);
    setUserRole(null);
    profileFetchedRef.current = false;
    saveAuthCache(null);
  }, [globalUser, globalProfile, setUser, setProfile, setIsProfileLoaded]);

  // Sets a new account's type before any profile load. Overlapping refreshes share one
  // attempt, which consumes the stored choice so it cannot race the prompt's answer.
  const settleUserType = useCallback(
    (attributes: FetchUserAttributesOutput): Promise<FetchUserAttributesOutput | null> => {
      if (attributes['custom:type']) {
        forgetPendingUserType(attributes.email);
        return Promise.resolve(attributes);
      }
      if (settlingRef.current) return settlingRef.current;

      settlingRef.current = (async () => {
        const pendingType = readPendingUserType(attributes.email);
        forgetPendingUserType(attributes.email);
        if (pendingType && mayChooseUserType(pendingType, attributes.email)) {
          try {
            await setUserType(pendingType);
            const { fetchUserAttributes } = await import('aws-amplify/auth');
            const settled = await fetchUserAttributes();
            if (settled['custom:type']) return settled;
          } catch (error) {
            logger.error(`${LOG_MODULE} Failed to set the user type chosen at sign-up`, { error });
          }
        }

        setUserTypeOptions(userTypeOptionsFor(attributes.email));
        setNeedsUserType(true);
        return null;
      })().finally(() => {
        settlingRef.current = null;
      });
      return settlingRef.current;
    },
    [],
  );

  const refreshUserAttributes = useCallback(async () => {
    if (!isAppRoute) return;

    try {
      await ensureAmplifyConfigured();
      const { getCurrentUser, fetchUserAttributes } = await import('aws-amplify/auth');

      const currentUser = await getCurrentUser().catch(() => null);
      if (!currentUser) {
        clearAuthState();
        return;
      }

      // `getCurrentUser()` returns Cognito userId (sub) in v6.
      cognitoUserIdRef.current =
        (currentUser as any).userId || (currentUser as any).username || null;

      const attributes = await settleUserType(await fetchUserAttributes());
      if (!attributes) return;
      setNeedsUserType(false);

      setUserAttributes(attributes);
      const role = attributes['custom:role']?.toLowerCase() || null;
      setUserRole(role);
      logger.debug(`${LOG_MODULE} Refreshed user attributes`, attributes);
    } catch (error) {
      // In practice this can happen during logout or expired sessions.
      logger.debug(`${LOG_MODULE} Failed to refresh user attributes`, error);
      clearAuthState();
    }
  }, [isAppRoute, clearAuthState, settleUserType]);

  const chooseUserType = useCallback(
    async (type: SelfServiceUserType) => {
      await setUserType(type);
      await refreshUserAttributes();
    },
    [refreshUserAttributes],
  );

  useEffect(() => {
    // Only attempt auth on app routes; this avoids pulling Amplify into the marketing bundle.
    if (!isAppRoute) return;

    const run = async () => {
      if (userAttributes) return;
      await refreshUserAttributes();
    };

    run();
  }, [isAppRoute, refreshUserAttributes, userAttributes]);

  useEffect(() => {
    if (isAppRoute && userAttributes) {
      const cognitoUserId = cognitoUserIdRef.current;
      if (!cognitoUserId) {
        logger.debug(`${LOG_MODULE} Missing Cognito userId; skipping user hydration`);
        return;
      }

      const currentUser: User = {
        id: cognitoUserId,
        type: userAttributes['custom:type'] as UserType,
        role: userAttributes['custom:role']?.toLowerCase(),
      };
      currentUser.personal_info = {
        email: userAttributes['email'] as string,
        first_name: userAttributes['custom:first_name'] as string,
        last_name: userAttributes['custom:last_name'] as string,
        gender: (userAttributes['custom:gender'] as UserGender) || undefined,
        age: userAttributes['custom:age'] ? Number(userAttributes['custom:age']) : undefined,
        institution: userAttributes['custom:institution'] as string,
      };

      // Log for debugging
      logger.debug(`${LOG_MODULE} Setting user with attributes`, {
        currentUser,
        attributesPresent: !!userAttributes['custom:first_name'],
      });

      setUser(currentUser);

      // Update cache when we get fresh user data
      saveAuthCache(currentUser);
    }
  }, [isAppRoute, userAttributes, setUser]);

  useEffect(() => {
    if (globalUser && !globalProfile && !profileFetchedRef.current) {
      fetchProfile(globalUser.id, globalUser.type);
    }
  }, [globalUser, globalProfile, fetchProfile]);

  // Refresh user attributes when the tab becomes active again (session might have changed).
  useEffect(() => {
    if (!isAppRoute) return;

    const onFocus = () => {
      refreshUserAttributes();
    };

    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [isAppRoute, refreshUserAttributes]);

  // React to Amplify auth events. Without this, AuthContext only learns about a
  // sign-in via the on-mount fetch (which short-circuits if no session existed
  // yet) or a window 'focus' event, leaving users on a stale "Welcome" screen
  // until they refocus the tab.
  useEffect(() => {
    if (!isAppRoute) return;
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    (async () => {
      await ensureAmplifyConfigured();
      const { Hub } = await import('aws-amplify/utils');
      if (cancelled) return;
      unsubscribe = Hub.listen('auth', ({ payload }) => {
        switch (payload.event) {
          case 'signedIn':
          case 'tokenRefresh':
            refreshUserAttributes();
            break;
          case 'signedOut':
            clearAuthState();
            forgetAllPendingUserTypes();
            break;
        }
      });
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [isAppRoute, refreshUserAttributes, clearAuthState]);

  // Check if provider profile is complete
  useEffect(() => {
    if (globalUser?.type === UserType.PROVIDER && globalProfile) {
      const providerProfile = globalProfile as ProviderProfile;
      const needsCompletion = providerProfileNeedsCompletion(providerProfile);

      setNeedsProviderProfileCompletion(needsCompletion);
    } else {
      setNeedsProviderProfileCompletion(false);
    }
  }, [globalUser, globalProfile]);

  const value = {
    isProfileLoading,
    refreshProfile,
    refreshUserAttributes,
    userRole,
    needsProviderProfileCompletion,
    needsUserType,
    userTypeOptions,
    chooseUserType,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
